<template>
  <div class="rounded-lg relative block leading-none h-full">
    <RevButtonBase
      ref="interactiveElement"
      v-bind="ariaLabel ? { 'aria-label': ariaLabel } : {}"
      class="rounded-lg absolute inset-0 flex items-center justify-center h-full"
      @click.stop="((isPlaying ? pauseVideo : playVideo)(), emit('click'))"
      @focusin="isHovering = true"
      @focusout="isHovering = false"
      @keydown.enter.stop="
        ((isPlaying ? pauseVideo : playVideo)(), emit('click'))
      "
      @mouseenter="isHovering = true"
      @mouseleave="isHovering = false"
    >
      <img
        v-if="!isPlaying && !videoStarted"
        :alt="title"
        class="rounded-lg aspect-square h-full object-cover"
        :class="imgClassName"
        :src="thumbnailUrl"
      />
      <div
        class="rounded-full absolute left-1/2 top-1/2 flex h-[68px] w-[68px] -translate-x-1/2 -translate-y-1/2 items-center justify-center"
        :class="{
          'bg-[#FFF]': !isPlaying || isHovering,
        }"
      >
        <IconMediaPlay v-if="!isPlaying || isVideoEnded" size="48" />
        <IconMediaPause v-else-if="isHovering" size="48" />
      </div>
    </RevButtonBase>
    <iframe
      v-if="isPlaying || videoStarted"
      id="youtube-iframe"
      ref="youtubeIframe"
      allow="autoplay; encrypted-media"
      allowfullscreen
      class="rounded-lg aspect-square h-full w-full object-cover"
      frameborder="0"
      :src="embedUrl"
      :title
      @load="emit('start')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, useTemplateRef, watch } from 'vue'

import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { IconMediaPause } from '@ds/icons/IconMediaPause'
import { IconMediaPlay } from '@ds/icons/IconMediaPlay'
import { useIntersectionObserver } from '@vueuse/core'

import type { CommonYouTubeCommands } from './YouTubeVideo.types'
import {
  YOUTUBE_VIDEO_ENDED_STATE,
  YOUTUBE_VIDEO_ID,
  YOUTUBE_VIDEO_ORIGIN,
  YOUTUBE_VIDEO_POSTER,
  YOUTUBE_VIDEO_TITLE,
  YOUTUBE_VIDEO_UNSTARTED_STATE,
} from './YoutubeVideo.constants'

const {
  videoId = YOUTUBE_VIDEO_ID,
  title = YOUTUBE_VIDEO_TITLE,
  isMobile,
} = defineProps<{
  videoId?: string
  title?: string
  ariaLabel?: string
  // Only used for tracking and for deciding the autoplay behavior
  isMobile: boolean
  imgClassName?: string
}>()

const emit = defineEmits<{
  (e: 'click'): void
  (e: 'start'): void
}>()

const isPlaying = ref(false)
const isHovering = ref(false)
const videoStarted = ref(false)
const isVideoEnded = ref(false)

const youtubeIframe = useTemplateRef('youtubeIframe')
const thumbnailUrl = YOUTUBE_VIDEO_POSTER

const embedUrl = computed(() => {
  // add autoplay only when isPlaying is true
  const baseUrl = `${YOUTUBE_VIDEO_ORIGIN}/embed/${videoId}`

  const queryParams = new URLSearchParams()

  // Add autoplay
  queryParams.set('autoplay', '1')
  // Enable "modest branding"
  queryParams.set('modestbranding', '1')
  // Disable related videos
  queryParams.set('rel', '0')
  // Enable inline playback for iOS devices
  queryParams.set('playsinline', '1')
  // Disable player controls
  queryParams.set('controls', '0')
  // Disable keyboard
  queryParams.set('disablekb', '1')
  // Hides video annotations
  queryParams.set('iv_load_policy', '3')
  // Enable jsapi (needed to communicate with the iframe via postMessage)
  queryParams.set('enablejsapi', '1')

  return `${baseUrl}?${queryParams.toString()}`
})

const { trackVideoImpression } = useTracking()

function sendYouTubeCommand(
  command: CommonYouTubeCommands,
  args: unknown[] = [],
) {
  youtubeIframe.value?.contentWindow?.postMessage(
    `{"event":"command","func":"${command}","args":${JSON.stringify(args)}}`,
    YOUTUBE_VIDEO_ORIGIN,
  )
}

function playVideo({ fromStart = false, muted = false } = {}) {
  isPlaying.value = true
  videoStarted.value = true
  isVideoEnded.value = false

  if (youtubeIframe.value) {
    if (fromStart) {
      const [
        // time in seconds the video should target
        seconds,
        // whether the player can make a request to
        // get the video (if no buffered data is available)
        allowSeekAhead,
      ] = [0, true]
      sendYouTubeCommand('seekTo', [seconds, allowSeekAhead])
    }
    if (muted) {
      sendYouTubeCommand('mute')
    }
    sendYouTubeCommand('playVideo')
  }
}

function pauseVideo() {
  isPlaying.value = false
  sendYouTubeCommand('pauseVideo')
}

const interactiveElement = useTemplateRef('interactiveElement')
const { stop: stopIntersectionObserver } = useIntersectionObserver(
  interactiveElement,
  ([{ isIntersecting, intersectionRatio }]) => {
    if (isIntersecting && intersectionRatio > 0.7 && isMobile) {
      if (!isPlaying.value) {
        playVideo({ fromStart: true, muted: true })
      }

      stopIntersectionObserver()
    } else if (isPlaying.value && isMobile) {
      pauseVideo()
    }

    if (isIntersecting && intersectionRatio > 0.9 && !isMobile) {
      if (!isPlaying.value) {
        playVideo({ fromStart: !videoStarted.value, muted: true })
      }
    } else if (isPlaying.value && !isMobile) {
      pauseVideo()
    }
  },
  {
    threshold: 0.7,
  },
)

function onMessage(event: MessageEvent) {
  // Check origin for security
  if (event.origin !== YOUTUBE_VIDEO_ORIGIN) {
    return
  }

  if (event.source === youtubeIframe.value?.contentWindow) {
    try {
      const data = JSON.parse(event.data)

      // if video is loaded, and it's not playing, play it
      // (we only get this after the iframe is loaded and the video is ready)
      if (data?.info?.playerState === YOUTUBE_VIDEO_UNSTARTED_STATE) {
        playVideo({ fromStart: true, muted: true })
      }

      // Check if video has ended
      if (data?.info?.playerState === YOUTUBE_VIDEO_ENDED_STATE) {
        isPlaying.value = false
        isVideoEnded.value = true

        trackVideoImpression({
          name: 'video_completed',
          pageType: 'product',
          zone: 'image_carousel',
        })
      }
    } catch (error) {
      // Silently handle parsing errors
    }
  }
}

let hoveringTimeout: ReturnType<typeof setTimeout>
const stopWatchingHovering = watch(isHovering, (isHoveringValue) => {
  if (isHoveringValue) {
    clearTimeout(hoveringTimeout)
    // we need to reset the hovering state after X seconds
    // because on mobile we don't fall in the `mouseleave` case
    hoveringTimeout = setTimeout(() => {
      isHovering.value = false
    }, 2000)
  }
})

const stopWatchingIframeValue = watch(youtubeIframe, (youtubeIframeValue) => {
  if (youtubeIframeValue) {
    // Add message listener when iframe is available
    window.addEventListener('message', onMessage, {
      passive: true,
    })

    // Wait for iframe to load before sending listening command
    const handleIframeLoad = () => {
      youtubeIframeValue.contentWindow?.postMessage(
        '{"event": "listening"}',
        YOUTUBE_VIDEO_ORIGIN,
      )
    }

    // Listen for iframe load event
    youtubeIframeValue.addEventListener('load', handleIframeLoad, {
      passive: true,
    })

    // If iframe is already loaded, send the command immediately
    if (youtubeIframeValue.contentDocument?.readyState === 'complete') {
      handleIframeLoad()
    }
  }
})

onUnmounted(() => {
  clearTimeout(hoveringTimeout)
  stopWatchingHovering()
  stopWatchingIframeValue()
  stopIntersectionObserver()
  window.removeEventListener('message', onMessage)
})

defineExpose({
  playVideo,
})
</script>
